import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useAuth, useCart } from "../context/Store";
import { fmt, mediaUrl } from "../lib/api";

export default function Cart() {
  const { user } = useAuth();
  const { cart, update, remove } = useCart();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="cart-login-prompt">
        <h1 className="font-display text-3xl font-bold">Sign in to view your cart</h1>
        <Link to="/auth?next=/cart" className="inline-block mt-6 rounded-full brand-gradient-bg text-white px-8 py-3.5 text-sm font-semibold">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="cart-page">
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Your cart</h1>
      {cart.items.length === 0 ? (
        <div className="text-center py-24" data-testid="cart-empty">
          <p className="font-display text-2xl text-brand-ink/50">Your cart is empty</p>
          <Link to="/shop" className="inline-block mt-6 rounded-full brand-gradient-bg text-white px-8 py-3.5 text-sm font-semibold" data-testid="cart-shop-link">
            Continue shopping
          </Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 divide-y divide-brand-ink/8">
            {cart.items.map(({ product: p, qty }) => (
              <div key={`${p.id}:${p.variant_id || "base"}`} className="py-6 flex gap-5" data-testid={`cart-item-${p.slug}`}>
                <Link to={`/product/${p.slug}`} className="h-24 w-24 rounded-2xl overflow-hidden bg-[#F4F4F5] shrink-0">
                  <img src={mediaUrl(p.images?.[0])} alt={p.name} className="h-full w-full bg-white object-contain" />
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-brand-ink/40 font-semibold">{p.brand}</p>
                  <Link to={`/product/${p.slug}`} className="font-display font-semibold text-brand-ink leading-snug">{p.name}</Link>
                  {p.selected_variant && <p className="mt-1 text-xs text-brand-ink/50">Option: {p.selected_variant}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center rounded-full border border-brand-ink/15">
                      <button data-testid={`cart-minus-${p.slug}`} onClick={() => qty > 1 ? update(p.id, qty - 1, p.variant_id) : remove(p.id, p.variant_id)} className="h-9 w-9 flex items-center justify-center"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-7 text-center text-sm font-semibold">{qty}</span>
                      <button data-testid={`cart-plus-${p.slug}`} onClick={() => update(p.id, Math.min(10, qty + 1), p.variant_id)} className="h-9 w-9 flex items-center justify-center"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <span className="font-display font-bold">{fmt(p.price * qty)}</span>
                  </div>
                </div>
                <button data-testid={`cart-remove-${p.slug}`} onClick={() => remove(p.id, p.variant_id)} className="self-start p-2 text-brand-ink/30 hover:text-rose-500 transition-colors">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="rounded-3xl bg-brand-ink/[0.03] p-8 h-fit" data-testid="cart-summary">
            <h2 className="font-display text-xl font-bold">Order summary</h2>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-brand-ink/60">Subtotal</span><span className="font-semibold">{fmt(cart.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-brand-ink/60">Shipping</span><span className="font-semibold">{cart.shipping === 0 ? "Free" : fmt(cart.shipping)}</span></div>
              {cart.shipping > 0 && <p className="text-xs text-brand-blue">Add {fmt(999 - cart.subtotal)} more for free shipping</p>}
              <div className="border-t border-brand-ink/10 pt-3 flex justify-between text-base">
                <span className="font-semibold">Total</span>
                <span className="font-display font-bold text-xl" data-testid="cart-total">{fmt(cart.total)}</span>
              </div>
            </div>
            <button onClick={() => navigate("/checkout")} data-testid="checkout-button"
              className="mt-8 w-full h-13 py-4 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(41,98,255,0.35)] hover:scale-[1.01] transition-transform">
              Proceed to Checkout <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
